import React, { useCallback, useEffect, useMemo, useRef } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import compact from "lodash/compact";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
/* eslint-disable max-lines */
import { useTranslation } from "react-i18next";

import { MetricRangeType } from "@waterly/graphql-types";

import { useAdvancedToggle } from "../../../hooks";
import Modal from "../Modal/Modal";
import { SeverityColor } from "../SeveritySelect/severity.utils";

import IdealRangeConfig from "./MetricRangeConfig/IdealRangeConfig";
import type { LowerUpperLimitRangeConfigProps } from "./MetricRangeConfig/LowerUpperLimitRangeConfig";
import LowerUpperLimitRangeConfig from "./MetricRangeConfig/LowerUpperLimitRangeConfig";
import type { MetricRangeSetupWizardProps } from "./MetricRangeSetupWizard";
import MetricRangeSetupWizard from "./MetricRangeSetupWizard";
import type { LimitRangeSide, MetricRangeCardMetricRange } from "./metricRangeUtils";
import {
  buildActiveMetricRangesWithAddedSideRange,
  findAdjacentMetricRangeForDeletion,
  getRegulatoryOperationalVisibility,
  metricRangeCardMetricRandKeyGenerator,
  moveSingletonMetricRangeTagToAdjacentRange,
  normalizeMetricRangeInclusivity,
  normalizeSingletonMetricRangeTags,
  preserveDeletedMetricRanges,
  sortMetricRangesByBounds,
  synchronizeCollapsedIdealRangeTags,
  synchronizeIdealSingletonAdjacentTags,
  updateMetricRangesWithBorderingRange,
} from "./metricRangeUtils";
import { useMetricRangeErrorsManager } from "./useMetricRangeErrorsManager";

const rangeColors = [SeverityColor.YELLOW, SeverityColor.ORANGE, SeverityColor.RED];

const limitRangeChangeConfig = {
  [MetricRangeType.Lower]: {
    boundaryKey: "rangeMax",
    borderingRangeBoundaryKey: "rangeMin",
    tagKey: "maxTagId",
    borderingRangeTagKey: "minTagId",
    borderingRangeOffset: 1,
    adjacentRangeOffset: -1,
  },
  [MetricRangeType.Upper]: {
    boundaryKey: "rangeMin",
    borderingRangeBoundaryKey: "rangeMax",
    tagKey: "minTagId",
    borderingRangeTagKey: "maxTagId",
    borderingRangeOffset: -1,
    adjacentRangeOffset: 1,
  },
} as const;

export type MetricRangeEditorProps = {
  cloneMetricRangesComponent?: MetricRangeSetupWizardProps["cloneMetricRangesComponent"];
  metricRangeType: MetricRangeType | null;
  //activeMetricRanges are metrics that are not deleted
  activeMetricRanges: Omit<MetricRangeCardMetricRange, "deletedAt">[];
  metricRanges: MetricRangeCardMetricRange[];
  onChange: (value: {
    metricRangeType: MetricRangeType | null;
    metricRanges: MetricRangeCardMetricRange[];
  }) => void;
  metricRangeTagOptions: LowerUpperLimitRangeConfigProps["metricRangeTagOptions"];
  devMode: boolean;
  onDraftErrorChange?: (hasError: boolean) => void;
};

const MetricRangeEditor = ({
  metricRanges,
  activeMetricRanges,
  metricRangeType,
  onChange,
  cloneMetricRangesComponent,
  metricRangeTagOptions,
  devMode,
  onDraftErrorChange,
}: MetricRangeEditorProps) => {
  const { t } = useTranslation(["common"]);
  const { metricRangeErrors, validateBeforeSave, idealMetricRangeErrors } =
    useMetricRangeErrorsManager(metricRanges);
  const showWizard = metricRangeType === null || activeMetricRanges.length === 0;
  const isLower = metricRangeType === MetricRangeType.Lower || metricRangeType === MetricRangeType.Both;
  const isUpper = metricRangeType === MetricRangeType.Upper || metricRangeType === MetricRangeType.Both;
  const idealRange = useMemo(() => activeMetricRanges.find((mr) => mr.ideal), [activeMetricRanges]);

  const hasDraftErrorForRange = useCallback(
    (range: MetricRangeCardMetricRange) =>
      Object.values((range.ideal ? idealMetricRangeErrors : metricRangeErrors[range.id]) ?? {}).some(Boolean),
    [idealMetricRangeErrors, metricRangeErrors],
  );

  const draftErrorRangeIds = useMemo(
    () =>
      new Set(activeMetricRanges.filter((range) => hasDraftErrorForRange(range)).map((range) => range.id)),
    [activeMetricRanges, hasDraftErrorForRange],
  );
  const draftErrorRangeIdsRef = useRef(draftErrorRangeIds);
  draftErrorRangeIdsRef.current = draftErrorRangeIds;
  const [
    isClearConfirmationOpen,
    { toggleTrue: handleOpenClearConfirmation, toggleFalse: handleCloseClearConfirmation },
  ] = useAdvancedToggle(false);

  const hasDraftError = draftErrorRangeIds.size > 0;
  const hasIdealDraftLimitError = Boolean(
    idealMetricRangeErrors[MetricRangeType.Lower] ?? idealMetricRangeErrors[MetricRangeType.Upper],
  );
  const hasDraftLimitError = useMemo(() => {
    if (hasIdealDraftLimitError) return true;

    return activeMetricRanges.some(
      (range) => !range.ideal && Boolean(metricRangeErrors[range.id]?.limitError),
    );
  }, [activeMetricRanges, hasIdealDraftLimitError, metricRangeErrors]);
  const hasOtherDraftError = useCallback((editedRangeId: string) => {
    const rangeIds = draftErrorRangeIdsRef.current;
    return rangeIds.size > (rangeIds.has(editedRangeId) ? 1 : 0);
  }, []);

  useEffect(() => {
    onDraftErrorChange?.(hasDraftError);
  }, [hasDraftError, onDraftErrorChange]);

  const { lowerLimitRanges, upperLimitRanges, unclassifiedRanges } = useMemo(() => {
    const nonIdealRanges = activeMetricRanges.filter((range) => !range.ideal);

    if (!idealRange) {
      return { lowerLimitRanges: [], upperLimitRanges: [], unclassifiedRanges: nonIdealRanges };
    }

    const lowerLimitRanges = nonIdealRanges
      .filter(
        (range) =>
          !isNil(idealRange.rangeMin) && !isNil(range.rangeMax) && range.rangeMax <= idealRange.rangeMin,
      )
      .sort(sortMetricRangesByBounds);
    const upperLimitRanges = nonIdealRanges
      .filter(
        (range) =>
          !isNil(idealRange.rangeMax) && !isNil(range.rangeMin) && range.rangeMin >= idealRange.rangeMax,
      )
      .sort(sortMetricRangesByBounds);
    const classifiedRangeIds = new Set([...lowerLimitRanges, ...upperLimitRanges].map((range) => range.id));
    const unclassifiedRanges = nonIdealRanges.filter((range) => !classifiedRangeIds.has(range.id));

    return { lowerLimitRanges, upperLimitRanges, unclassifiedRanges };
  }, [idealRange, activeMetricRanges]);

  const tagIndexes = useMemo(() => {
    const regulatoryId = metricRangeTagOptions.find((mrto) => mrto.additionalProps?.isRegulatory)?.id;
    const operationalId = metricRangeTagOptions.find((mrto) => mrto.additionalProps?.isOperational)?.id;

    return {
      [MetricRangeType.Lower]: {
        regulatoryIndex: lowerLimitRanges.findIndex((llr) => llr.maxTagId === regulatoryId),
        operationalIndex: lowerLimitRanges.findIndex((llr) => llr.maxTagId === operationalId),
      },
      [MetricRangeType.Upper]: {
        operationalIndex: upperLimitRanges.findIndex((llr) => llr.minTagId === operationalId),
        regulatoryIndex: upperLimitRanges.findIndex((llr) => llr.minTagId === regulatoryId),
      },
    } as const;
  }, [lowerLimitRanges, metricRangeTagOptions, upperLimitRanges]);

  const showRegulatorOrOperational = useCallback(
    ({
      metricRangeSide,
      index,
    }: {
      index: number;
      metricRangeSide: MetricRangeType.Upper | MetricRangeType.Lower;
    }) => {
      return getRegulatoryOperationalVisibility({
        metricRangeSide,
        index,
        regulatoryIndex: tagIndexes[metricRangeSide].regulatoryIndex,
        operationalIndex: tagIndexes[metricRangeSide].operationalIndex,
      });
    },
    [tagIndexes],
  );

  const handleResetMetricRanges = useCallback(() => {
    const updatedMetricRanges = metricRanges
      .filter((mr) => !mr.isNew) //remove metric ranges that have been created but not yet saved
      .map((mr) => ({ ...mr, deletedAt: new Date().toISOString() }));
    onChange({ metricRangeType: null, metricRanges: updatedMetricRanges });
    handleCloseClearConfirmation();
  }, [handleCloseClearConfirmation, metricRanges, onChange]);

  const handleIdealRangeChange = useCallback(
    (side: LimitRangeSide) => (newMetricRange: MetricRangeCardMetricRange) => {
      if (!idealRange) return;
      const isDuplicateBoundarySync = isEqual(newMetricRange, idealRange);

      if (hasOtherDraftError(newMetricRange.id) && isDuplicateBoundarySync) {
        return;
      }

      let updatedMetricRanges = updateMetricRangesWithBorderingRange({
        metricRanges: activeMetricRanges,
        newMetricRange,
        borderingRange: lowerLimitRanges[lowerLimitRanges.length - 1],
        oldBoundary: idealRange.rangeMin,
        newBoundary: newMetricRange.rangeMin,
        borderingRangeBoundaryKey: "rangeMax",
        newMetricRangeTagKey: "minTagId",
        borderingRangeTagKey: "maxTagId",
      });

      updatedMetricRanges = updateMetricRangesWithBorderingRange({
        metricRanges: updatedMetricRanges,
        newMetricRange,
        borderingRange: upperLimitRanges[0],
        oldBoundary: idealRange.rangeMax,
        newBoundary: newMetricRange.rangeMax,
        borderingRangeBoundaryKey: "rangeMin",
        newMetricRangeTagKey: "maxTagId",
        borderingRangeTagKey: "minTagId",
      });

      updatedMetricRanges = normalizeSingletonMetricRangeTags({
        metricRanges: updatedMetricRanges,
        retainedTags: [
          [lowerLimitRanges[lowerLimitRanges.length - 1]?.id, "maxTagId"],
          [upperLimitRanges[0]?.id, "minTagId"],
        ],
      });

      updatedMetricRanges = synchronizeCollapsedIdealRangeTags({
        metricRanges: updatedMetricRanges,
        previousIdealRange: idealRange,
        newIdealRange: newMetricRange,
        lowerAdjacentRangeId: lowerLimitRanges[lowerLimitRanges.length - 1]?.id,
        upperAdjacentRangeId: upperLimitRanges[0]?.id,
      });

      const proposedMetricRanges = preserveDeletedMetricRanges({
        metricRanges,
        updatedActiveMetricRanges: updatedMetricRanges,
      });
      const { success } = validateBeforeSave({
        proposedRanges: proposedMetricRanges,
        editedRangeId: newMetricRange.id,
        side,
      });

      if (!success) return;

      onChange({
        metricRangeType,
        metricRanges: proposedMetricRanges,
      });
    },
    [
      idealRange,
      lowerLimitRanges,
      metricRangeType,
      metricRanges,
      activeMetricRanges,
      onChange,
      upperLimitRanges,
      validateBeforeSave,
      hasOtherDraftError,
    ],
  );

  const handleIdealLowerLimitChange = useMemo(
    () => handleIdealRangeChange(MetricRangeType.Lower),
    [handleIdealRangeChange],
  );
  const handleIdealUpperLimitChange = useMemo(
    () => handleIdealRangeChange(MetricRangeType.Upper),
    [handleIdealRangeChange],
  );

  const handleLimitRangeChange = useCallback(
    (side: LimitRangeSide) => (newMetricRange: MetricRangeCardMetricRange) => {
      const sideRanges = {
        [MetricRangeType.Lower]: lowerLimitRanges,
        [MetricRangeType.Upper]: upperLimitRanges,
      }[side];
      const {
        boundaryKey,
        borderingRangeBoundaryKey,
        tagKey,
        borderingRangeTagKey,
        borderingRangeOffset,
        adjacentRangeOffset,
      } = limitRangeChangeConfig[side];
      const currentRangeIndex = sideRanges.findIndex((range) => range.id === newMetricRange.id);
      const currentRange = sideRanges[currentRangeIndex];
      if (
        hasOtherDraftError(newMetricRange.id) &&
        currentRange?.[boundaryKey] === newMetricRange[boundaryKey]
      ) {
        return;
      }

      const oldBoundary = sideRanges[currentRangeIndex]?.[boundaryKey];
      const newBoundary = newMetricRange[boundaryKey];
      const borderingRange =
        sideRanges[currentRangeIndex + borderingRangeOffset] ??
        (oldBoundary === idealRange?.[borderingRangeBoundaryKey] ? idealRange : undefined);

      let updatedMetricRanges = updateMetricRangesWithBorderingRange({
        metricRanges: activeMetricRanges,
        newMetricRange,
        borderingRange,
        oldBoundary,
        newBoundary,
        borderingRangeBoundaryKey,
        newMetricRangeTagKey: tagKey,
        borderingRangeTagKey,
      });

      if (borderingRange?.ideal) {
        updatedMetricRanges = synchronizeIdealSingletonAdjacentTags({
          metricRanges: updatedMetricRanges,
          idealRangeId: borderingRange.id,
          lowerAdjacentRangeId: lowerLimitRanges[lowerLimitRanges.length - 1]?.id,
          upperAdjacentRangeId: upperLimitRanges[0]?.id,
          retainedIdealTagKey: borderingRangeTagKey,
          tagId: newMetricRange[tagKey],
        });
      }

      updatedMetricRanges = moveSingletonMetricRangeTagToAdjacentRange({
        metricRanges: updatedMetricRanges,
        singletonRangeId: newMetricRange.id,
        sourceTagKey: tagKey,
        adjacentRangeId: sideRanges[currentRangeIndex + adjacentRangeOffset]?.id,
        adjacentTagKey: tagKey,
      });

      const proposedMetricRanges = preserveDeletedMetricRanges({
        metricRanges,
        updatedActiveMetricRanges: updatedMetricRanges,
      });

      const { success } = validateBeforeSave({
        proposedRanges: proposedMetricRanges,
        editedRangeId: newMetricRange.id,
        side,
      });

      if (!success) return;

      onChange({
        metricRangeType,
        metricRanges: proposedMetricRanges,
      });
    },
    [
      lowerLimitRanges,
      upperLimitRanges,
      idealRange,
      activeMetricRanges,
      metricRanges,
      validateBeforeSave,
      onChange,
      metricRangeType,
      hasOtherDraftError,
    ],
  );

  const handleAddRange = useCallback(
    (side: MetricRangeType.Lower | MetricRangeType.Upper) => () => {
      if (hasDraftError) return;

      const sideRanges = side === MetricRangeType.Lower ? lowerLimitRanges : upperLimitRanges;
      const updatedActiveMetricRanges = buildActiveMetricRangesWithAddedSideRange({
        side,
        sideRanges,
        idealRange,
        activeMetricRanges,
        newRangeName: t("common:new_range"),
        newRangeColor: rangeColors[sideRanges.length % 3],
      });
      if (!updatedActiveMetricRanges) return;

      onChange({
        metricRangeType,
        metricRanges: preserveDeletedMetricRanges({ metricRanges, updatedActiveMetricRanges }),
      });
    },
    [
      lowerLimitRanges,
      upperLimitRanges,
      idealRange,
      metricRanges,
      onChange,
      metricRangeType,
      t,
      activeMetricRanges,
      hasDraftError,
    ],
  );

  const handleOnDeleteRange = useCallback(
    ({
        range,
        metricRangeTypeSide,
      }: {
        range: MetricRangeCardMetricRange;
        metricRangeTypeSide: MetricRangeType.Lower | MetricRangeType.Upper;
      }) =>
      () => {
        let modifiedAdjacentRangeToModify: MetricRangeCardMetricRange | undefined = undefined;

        if (metricRangeTypeSide === MetricRangeType.Lower) {
          const adjacentRangeToModify = findAdjacentMetricRangeForDeletion({
            ranges: lowerLimitRanges,
            range,
            metricRangeTypeSide,
          });
          if (!adjacentRangeToModify) return;
          modifiedAdjacentRangeToModify = {
            ...adjacentRangeToModify,
            rangeMin: range.rangeMin,
            rangeMinInclusive: range.rangeMinInclusive,
            minTagId: range.minTagId,
          };
        } else {
          const adjacentRangeToModify = findAdjacentMetricRangeForDeletion({
            ranges: upperLimitRanges,
            range,
            metricRangeTypeSide,
          });
          if (!adjacentRangeToModify) return;
          modifiedAdjacentRangeToModify = {
            ...adjacentRangeToModify,
            rangeMax: range.rangeMax,
            rangeMaxInclusive: range.rangeMaxInclusive,
            maxTagId: range.maxTagId,
          };
        }

        if (!modifiedAdjacentRangeToModify) return;

        const updatedMetricRanges = compact(
          metricRanges.map((mr) => {
            //modifies/removes the metric range that was selected to be deleted
            if (mr.id === range.id) {
              if (mr.isNew) return undefined;
              return {
                ...mr,
                deletedAt: new Date().toISOString(),
              };
            }
            if (modifiedAdjacentRangeToModify.id === mr.id) {
              return modifiedAdjacentRangeToModify;
            }
            return mr;
          }),
        );

        onChange({
          metricRangeType,
          metricRanges: preserveDeletedMetricRanges({
            metricRanges: updatedMetricRanges,
            updatedActiveMetricRanges: normalizeMetricRangeInclusivity(
              updatedMetricRanges.filter((metricRange) => !metricRange.deletedAt),
            ),
          }),
        });
      },
    [metricRanges, onChange, metricRangeType, lowerLimitRanges, upperLimitRanges],
  );

  const handleWizardOnChange = useCallback<MetricRangeSetupWizardProps["onChange"]>(
    (value) => {
      const { metricRangeType: newMetricRangeType, metricRanges: newMetricRanges } = value;

      //the wizard replaces the whole configuration, so persisted ranges that are still active must be
      //soft-deleted (the backend treats omitted existing ranges as unchanged) and unsaved ones removed
      const replacedMetricRanges = metricRanges
        .filter((metricRange) => !metricRange.isNew)
        .map((metricRange) =>
          metricRange.deletedAt ? metricRange : { ...metricRange, deletedAt: new Date().toISOString() },
        );

      onChange({
        metricRangeType: newMetricRangeType,
        metricRanges: [...newMetricRanges, ...replacedMetricRanges],
      });
    },
    [metricRanges, onChange],
  );

  const clearButton = useMemo(
    () => (
      <Box display="flex" justifyContent="flex-end">
        <Button
          startIcon={<DeleteIcon />}
          color="error"
          variant="contained"
          onClick={handleOpenClearConfirmation}
        >
          {t("common:clear")}
        </Button>
      </Box>
    ),
    [handleOpenClearConfirmation, t],
  );

  const clearConfirmationModal = useMemo(
    () => (
      <Modal
        open={isClearConfirmationOpen}
        onClose={handleCloseClearConfirmation}
        title={t("common:clear_metric_ranges")}
        actions={
          <>
            <Button onClick={handleCloseClearConfirmation}>{t("common:cancel")}</Button>
            <Button
              startIcon={<DeleteIcon />}
              color="error"
              variant="contained"
              onClick={handleResetMetricRanges}
            >
              {t("common:clear")}
            </Button>
          </>
        }
      >
        <Typography>{t("common:clear_metric_ranges_confirmation")}</Typography>
      </Modal>
    ),
    [handleCloseClearConfirmation, handleResetMetricRanges, isClearConfirmationOpen, t],
  );

  if (showWizard) {
    return (
      <Stack gap={2}>
        <MetricRangeSetupWizard
          cloneMetricRangesComponent={cloneMetricRangesComponent}
          metricRangeType={metricRangeType}
          onChange={handleWizardOnChange}
          metricRangeTagOptions={metricRangeTagOptions}
          devMode={devMode}
        />
      </Stack>
    );
  }

  //the existing ranges cannot be represented by the editor (no ideal range, ranges that overlap/straddle
  //the ideal range, or ranges on a side the metricRangeType does not allow), so offer a reset instead of
  //silently hiding ranges that would still be included in saves
  const needsRepair =
    !idealRange ||
    unclassifiedRanges.length > 0 ||
    (lowerLimitRanges.length > 0 && !isLower) ||
    (upperLimitRanges.length > 0 && !isUpper);

  if (needsRepair) {
    return (
      <Stack gap={2}>
        {clearButton}
        {clearConfirmationModal}
        <Typography color="error">{t("common:metric_ranges_need_repair")}</Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {clearButton}
      {clearConfirmationModal}
      {isLower && (
        <Button
          fullWidth
          variant="outlined"
          onClick={handleAddRange(MetricRangeType.Lower)}
          disabled={hasDraftError}
        >
          {t("common:add_lower_zone")}
        </Button>
      )}
      <Box>
        <Divider>
          <Typography>{t("common:no_limits_beyond_this")}</Typography>
        </Divider>
        {lowerLimitRanges.map((llr, index) => {
          const currentMin = llr?.rangeMin ?? -Infinity;
          const nextMetricRange = lowerLimitRanges[index + 1] ?? idealRange;
          const nextMetricRangeMax = nextMetricRange.rangeMax ?? Infinity;
          const { showRegulatory, showOperational } = showRegulatorOrOperational({
            metricRangeSide: MetricRangeType.Lower,
            index,
          });
          return (
            <LowerUpperLimitRangeConfig
              metricRangeType={MetricRangeType.Lower}
              key={metricRangeCardMetricRandKeyGenerator(llr)}
              range={llr}
              onChange={handleLimitRangeChange(MetricRangeType.Lower)}
              onDelete={handleOnDeleteRange({ range: llr, metricRangeTypeSide: MetricRangeType.Lower })}
              bordersIdeal={index === lowerLimitRanges.length - 1}
              nameErrorMessage={metricRangeErrors[llr.id]?.nameError}
              limitErrorMessage={metricRangeErrors[llr.id]?.limitError}
              min={currentMin}
              max={nextMetricRangeMax}
              disableName={hasDraftLimitError}
              disableRegulatory={!showRegulatory || hasDraftError}
              disableOperational={!showOperational || hasDraftError}
              metricRangeTagOptions={metricRangeTagOptions}
              devMode={devMode}
            />
          );
        })}
      </Box>
      <IdealRangeConfig
        metricRangeType={metricRangeType}
        idealRange={idealRange}
        onLowerLimitChange={handleIdealLowerLimitChange}
        onUpperLimitChange={handleIdealUpperLimitChange}
        metricRangeTagOptions={metricRangeTagOptions}
        lowerLimitErrorMessage={idealMetricRangeErrors[MetricRangeType.Lower]}
        upperLimitErrorMessage={idealMetricRangeErrors[MetricRangeType.Upper]}
        allowIncompleteLimits
        devMode={devMode}
      />
      <Box>
        {upperLimitRanges.map((ulr, index) => {
          const priorMetricRange = upperLimitRanges[index - 1] ?? idealRange;
          const priorMetricRangeMin = priorMetricRange.rangeMin ?? -Infinity;
          const currentMax = ulr.rangeMax ?? Infinity;
          const { showRegulatory, showOperational } = showRegulatorOrOperational({
            metricRangeSide: MetricRangeType.Upper,
            index,
          });
          return (
            <LowerUpperLimitRangeConfig
              metricRangeType={MetricRangeType.Upper}
              key={metricRangeCardMetricRandKeyGenerator(ulr)}
              range={ulr}
              onChange={handleLimitRangeChange(MetricRangeType.Upper)}
              onDelete={handleOnDeleteRange({ range: ulr, metricRangeTypeSide: MetricRangeType.Upper })}
              bordersIdeal={index === 0}
              nameErrorMessage={metricRangeErrors[ulr.id]?.nameError}
              limitErrorMessage={metricRangeErrors[ulr.id]?.limitError}
              min={priorMetricRangeMin}
              max={currentMax}
              metricRangeTagOptions={metricRangeTagOptions}
              devMode={devMode}
              disableName={hasDraftLimitError}
              disableOperational={!showOperational || hasDraftError}
              disableRegulatory={!showRegulatory || hasDraftError}
            />
          );
        })}
        <Divider>
          <Typography>{t("common:no_limits_beyond_this")}</Typography>
        </Divider>
      </Box>
      {isUpper && (
        <Button
          fullWidth
          variant="outlined"
          onClick={handleAddRange(MetricRangeType.Upper)}
          disabled={hasDraftError}
        >
          {t("common:add_upper_zone")}
        </Button>
      )}
    </Stack>
  );
};

export default MetricRangeEditor;
